import { Dtos, IndividualContactId, SuretyAccountId } from "@inrev/common";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { equals } from "rambda";
import { useRequest } from "../../utils/request";
import { accountIndividualsQueryKey, accountQueryKey } from "./account.keys";
import { IndividualData, SaveIndividualInput } from "./types";

export const useSaveAccountIndividual = (accountId: SuretyAccountId) => {
	const { post, put, _delete } = useRequest();
	const queryClient = useQueryClient();

	const individualsQueryKey = accountIndividualsQueryKey(accountId);
	const baseUrl = `/v2/surety/accounts/${accountId}/individuals`;

	const createIndividual = (data: IndividualData) =>
		post<Dtos.Individual.Get.Response>(baseUrl, data);

	const updateIndividual = (
		individualId: IndividualContactId,
		data: Dtos.Individual.Update.Request,
	) => put<Dtos.Individual.Get.Response>(`${baseUrl}/${individualId}`, data);

	const deleteIndividual = async (individualId: IndividualContactId): Promise<void> => {
		await _delete(`${baseUrl}/${individualId}`, undefined, "none");
	};

	const toComparable = (data: IndividualData | Dtos.Individual.Get.Response) => ({
		firstName: data.firstName,
		lastName: data.lastName,
		middleInitial: data.middleInitial ?? undefined,
		suffix: data.suffix ?? undefined,
		address: data.address ?? undefined,
		ssn: data.ssn ?? undefined,
		email: data.email ?? undefined,
		phone: data.phone ?? undefined,
		type: data.type ?? undefined,
		ownsAHome: data.ownsAHome ?? undefined,
		creditReportPermission: data.creditReportPermission ?? undefined,
	});

	const hasChanges = (data: IndividualData, existing: Dtos.Individual.Get.Response): boolean => {
		return !equals(toComparable(data), toComparable(existing));
	};

	const mutation = useMutation({
		mutationFn: async ({
			editingIndividualId,
			existingSpouseId,
			ownerData,
			spouseData,
		}: SaveIndividualInput) => {
			if (editingIndividualId) {
				const cachedIndividuals =
					queryClient.getQueryData<Dtos.Individual.Get.Response[]>(individualsQueryKey);
				const cachedOwner = cachedIndividuals?.find((i) => i.id === editingIndividualId);

				if (!cachedOwner || hasChanges(ownerData, cachedOwner)) {
					await updateIndividual(editingIndividualId, ownerData);
				}

				if (spouseData && existingSpouseId) {
					const cachedSpouse = cachedIndividuals?.find((i) => i.id === existingSpouseId);
					if (!cachedSpouse || hasChanges(spouseData, cachedSpouse)) {
						await updateIndividual(existingSpouseId, spouseData);
					}
				} else if (spouseData && !existingSpouseId) {
					await createIndividual({ ...spouseData, spouseId: editingIndividualId });
				} else if (!spouseData && existingSpouseId) {
					await deleteIndividual(existingSpouseId);
				}
			} else {
				const owner = await createIndividual(ownerData);

				if (spouseData) {
					await createIndividual({ ...spouseData, spouseId: owner.id });
				}
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: individualsQueryKey });
			queryClient.invalidateQueries({ queryKey: accountQueryKey(accountId) });
		},
	});

	return {
		saveAccountIndividual: mutation.mutate,
		saveAccountIndividualIsLoading: mutation.isPending,
		saveAccountIndividualError: mutation.error,
	};
};
